import React, { useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  TextField,
  Grid,
  Button,
  TableCell,
  Paper,
  Input,
  Popper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Checkbox,
  Skeleton,
  Chip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/CloseRounded";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Description as DocumentIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import PropTypes from "prop-types";
import { Formik } from "formik";
import * as Yup from "yup";
import ConfirmationDialogComponent from "src/components/Dialog/confirmation/confirmationDialog.component";
// Rev 4: a company holds exactly one bundle, and a bundle is a set of modules. This panel
// edits that set with the SAME widget the standalone company bundle dialog uses — it used to
// draw its own read-only grid with a hardcoded disabled switch, which is why "companies
// feature can't edit bundle content".
import BundleSelectionEditor, {
  NO_BUNDLE,
  bundleSelectionInitialValues,
  validateBundleSelection,
} from "src/modules/bundles/components/BundleSelectionEditor";
import CompanyFeaturePanel from "src/components/CompanyFeaturePanel";
import DataTable from "../../../../../components/DataTable";
import SearchableSelect from "../../../../../components/SearchableSelect";
import UserCreditTransferContainer from "../../forms/user-credit-transfer";
import CompanyWhiteLabelDialog from "../company-white-label-dialog";
import Loading from "../../../../../components/Loading";
/* eslint-disable */

const style = (theme) => ({
  paper: {
    backgroundColor: "rgb(90, 149, 89)",
    color: theme.palette.common.white,
    borderRadius: "4px",
  },
  boxSubmenu: {
    "&::before": {
      content: '""',
      position: "absolute",
      width: "0",
      height: "0",
      top: "8px",
      right: "-15px",
      border: "8px solid",
      borderColor: "transparent transparent transparent",
      borderLeftColor: "rgb(90, 149, 89)",
      zIndex: 1,
    },
  },
  divider: {
    height: "70%",
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(0, 1),
  },
  menuItem: {
    fontSize: "small",
    display: "block",
  },
});

const useStyles = makeStyles(style);
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      style={{ height: "100%", overflow: "hidden" }}
      {...other}
    >
      {value === index && (
        <Box sx={{ height: "100%", overflow: "hidden" }}>{children}</Box>
      )}
    </div>
  );
}

const CompanyDetailsDialogComponent = ({
  industries,
  selectedCompany,
  onCompanyDetailsDialogCloseClick,
  currentTab,
  onTabChange,
  onCompanyWhiteLabelDialogSubmitClick,
  onRemoveCompanyWhiteLabelClick,
  onCompanyUsersViewClick,
  selectedCompanyForUsers,
  companyUsers,
  totalUsers,
  currentUsersPage,
  isCompanyUsersLoading,
  completeDialogErrors,
  usersRowsPerPage,
  query,
  selectedCompanyUser,
  openCreditTransferConfirmation,
  isSubmitting,
  openCompleteDialog,
  onCompanyUsersPageClick,
  onClearFilter,
  handleUsersItemPerPageChange,
  onSearchSubmit,
  onCreditTransferClick,
  onCreditTransferConfirmationClick,
  onCompleteDialogOpen,
  onCreditTransferCloseClick,
  onCreditTransferConfirmationCloseClick,
  onCompleteDialogClose,
  onCreditTransferConfirmSubmitClick,
  handleImpersonate,
  canImpersonate,
  canTransferCredit,
  companyBranch,
  companyManageBranchPage,
  totalCompanyManageBranch,
  selectedBranchWhiteLabel,
  rowsPerPage,
  currentBranchPage,
  confirmationDialogModal,
  isRemovingBranch,
  isManageBranchLoading,
  onCompanyManageBranchPageClick,
  handleItemPerPageChange,
  onBranchWhiteLabelClick,
  onBranchWhiteLabelDialogSubmitClick,
  onBranchWhiteLabelDialogCloseClick,
  closeConfirmationDialog,
  setConfirmationDialog,
  onRemoveBranchWhiteLabelClick,
  // Expired Management props
  editMode,
  setEditMode,
  monthsInput,
  setMonthsInput,
  companyData,
  loadingExpired,
  updatingExpired,
  handleSaveExpiredDate,
  // Sales Email props
  isSubmittingSalesEmail,
  handleSalesEmailSubmit,
  // Company Features props
  bundleOptions,
  moduleListing,
  featureStructure,
  companyFeature,
  companyFeatureError,
  isCompanyFeatureLoaded,
  hasCompanyFeatureLoadFailed,
  isCompanyFeatureLoading,
  isUpdatingCompanyFeature,
  handleCompanyFeatureSubmit,
  handleCompanyFeatureRetry,
  // Swap Plan props
  planInfo,
  plans,
  isLoadingSwapPlan,
  isUpdatingSwapPlan,
  handleSwapPlanSubmit,
  // Tag Configuration props
  companyTags,
  isLoadingTags,
  isUpdatingTags,
  handleTagSubmit,
}) => {
  const classes = useStyles();
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedUserIndex, setSelectedUserIndex] = useState(null);

  if (!selectedCompany) {
    return null;
  }
  const rowsPerPageOption = [10, 25, 50];

  const getCreditAmount = (row) => {
    if (selectedCompanyForUsers && selectedCompanyForUsers.companyPlan === "Free Saas") {
      return "-";
    }
    const amount = row.creditInfo ? row.creditInfo.amount : 0;
    return new Intl.NumberFormat().format(amount);
  };

  const handleActionMenuOpen = (event, rowIndex) => {
    setSelectedUserIndex(rowIndex);
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setSelectedUserIndex(null);
    setActionMenuAnchor(null);
  };

  const renderCompanyDetailsPanel = () => {
    const renderSection = (title, icon, children) => (
      <Card
        sx={{
          mb: 3,
          border: "1px solid #e0e0e0",
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: "rgb(90, 149, 89)",
                color: "white",
                mr: 2,
                width: 40,
                height: 40,
              }}
            >
              {icon}
            </Avatar>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {title}
            </Typography>
          </Box>
          {children}
        </CardContent>
      </Card>
    );

    return (
      <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
        {/* Header */}
        <Card
          sx={{
            mb: 3,
            background:
              "linear-gradient(135deg, rgb(90, 149, 89) 0%, rgb(70, 120, 70) 100%)",
            color: "white",
          }}
        >
          <CardContent sx={{ py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Company Information
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              View detailed information about{" "}
              {(selectedCompany && selectedCompany.company && selectedCompany.company.name) || "this company"}
            </Typography>
          </CardContent>
        </Card>

        {/* Owner Details Section */}
        {renderSection(
          "Owner Details",
          <PersonIcon />,
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={(selectedCompany.owner && selectedCompany.owner.firstName) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={(selectedCompany.owner && selectedCompany.owner.lastName) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact"
                value={(selectedCompany.owner && selectedCompany.owner.contact) || "-"}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={(selectedCompany.owner && selectedCompany.owner.email) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Timezone"
                value={(selectedCompany.owner && selectedCompany.owner.timezone) || "-"}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        )}

        {/* Company Details Section */}
        {renderSection(
          "Company Details",
          <BusinessIcon />,
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={(selectedCompany.company && selectedCompany.company.name) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plan"
                value={(selectedCompany.company && selectedCompany.company.plan) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration No."
                value={(selectedCompany.company && selectedCompany.company.regNo) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Industry"
                value={
                  ((industries || []).find(
                    (i) =>
                      Number(i.id)
                      === Number(
                        selectedCompany.company
                          ? selectedCompany.company.industry
                          : undefined
                      )
                  ) || {}).name || ""
                }
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={(selectedCompany.company && selectedCompany.company.email) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact"
                value={(selectedCompany.company && selectedCompany.company.contact) || "-"}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={(selectedCompany.company && selectedCompany.company.addressLine1) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={(selectedCompany.company && selectedCompany.company.addressLine2) || "-"}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={(selectedCompany.company && selectedCompany.company.city) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={(selectedCompany.company && selectedCompany.company.state) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Postal Code"
                value={(selectedCompany.company && selectedCompany.company.postcode) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Country"
                value={(selectedCompany.company && selectedCompany.company.country) || ""}
                variant="outlined"
                disabled
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        )}

        {/* Documents Section */}
        {renderSection(
          "Company Documents",
          <DocumentIcon />,
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Article of Association
                </Typography>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() =>
                    window.open(
                      selectedCompany.company
                        ? selectedCompany.company.articleOfAssoclationUrl
                        : undefined,
                      "_blank"
                    )
                  }
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    borderColor: "rgb(90, 149, 89)",
                    color: "rgb(90, 149, 89)",
                    "&:hover": {
                      borderColor: "rgb(70, 120, 70)",
                      backgroundColor: "rgba(90, 149, 89, 0.04)",
                    },
                  }}
                >
                  📄 View Document
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Director's Identification
                </Typography>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() =>
                    window.open(
                      selectedCompany.company
                        ? selectedCompany.company.directorIdentificationUrl
                        : undefined,
                      "_blank"
                    )
                  }
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    borderColor: "rgb(90, 149, 89)",
                    color: "rgb(90, 149, 89)",
                    "&:hover": {
                      borderColor: "rgb(70, 120, 70)",
                      backgroundColor: "rgba(90, 149, 89, 0.04)",
                    },
                  }}
                >
                  📄 View Document
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    );
  };

  const renderConfigurationTab = () => (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background:
            "linear-gradient(135deg, rgb(90, 149, 89) 0%, rgb(70, 120, 70) 100%)",
          color: "white",
        }}
      >
        <CardContent sx={{ py: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Company Configuration
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Configure settings for this company
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Formik
            initialValues={{
              whiteLabel: (selectedCompany && selectedCompany.company && selectedCompany.company.whiteLabel) || "https://",
            }}
            validationSchema={Yup.object().shape({
              whiteLabel: Yup.string().matches(
                /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._~#=]{2,256}\.[a-z]{2,6}(?![^\s])\b(?:$|(?=\/))/,
                "Invalid white label"
              ),
            })}
            onSubmit={(values, { setSubmitting }) => {
              onCompanyWhiteLabelDialogSubmitClick(values, { setSubmitting });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      label="White Label URL"
                      name="whiteLabel"
                      fullWidth
                      value={values.whiteLabel}
                      required
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      error={touched.whiteLabel && errors.whiteLabel}
                      helperText={touched.whiteLabel && errors.whiteLabel}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  </Grid>

                  {/* API Access Credentials - Read Only */}
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        color: "text.secondary",
                        fontWeight: 600,
                      }}
                    >
                      API Access Credentials (Read Only)
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      variant="outlined"
                      label="API Access Key"
                      fullWidth
                      value={(selectedCompany && selectedCompany.company && selectedCompany.company.api_access_key) || "-"}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      variant="outlined"
                      label="API Access Secret"
                      fullWidth
                      value={(selectedCompany && selectedCompany.company && selectedCompany.company.api_access_secret) || "-"}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                      }}
                    >
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={isSubmitting}
                        sx={{
                          backgroundColor: "rgb(90, 149, 89)",
                          "&:hover": {
                            backgroundColor: "rgba(90, 149, 89, 0.8)",
                          },
                        }}
                      >
                        {isSubmitting ? "Saving..." : "Save"}
                      </Button>
                      {selectedCompany && selectedCompany.company && selectedCompany.company.whiteLabel && (
                        <Button
                          variant="contained"
                          sx={{
                            backgroundColor: "#d32f2f",
                            "&:hover": {
                              backgroundColor: "#c62828",
                            },
                          }}
                          onClick={onRemoveCompanyWhiteLabelClick}
                          disabled={isSubmitting}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </CardContent>
      </Card>

      {/* Tag Configuration Section */}
      <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 3, mt: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ mb: 3, fontWeight: 600, color: "text.primary" }}
          >
            Tag Configuration
          </Typography>

          {isLoadingTags ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Formik
              initialValues={{
                tags: companyTags || [],
                newTag: "",
              }}
              enableReinitialize
              onSubmit={(values, { setSubmitting }) => {
                handleTagSubmit(values);
                setSubmitting(false);
              }}
            >
              {({
                values,
                handleSubmit,
                setFieldValue,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Tag Display Area */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1.5, color: "text.secondary" }}
                      >
                        Current Tags
                      </Typography>
                      <Box
                        sx={{
                          minHeight: 80,
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 2,
                          backgroundColor: "#f9f9f9",
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          alignItems: values.tags.length === 0 ? "center" : "flex-start",
                          justifyContent: values.tags.length === 0 ? "center" : "flex-start",
                        }}
                      >
                        {values.tags.length === 0 ? (
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", fontStyle: "italic" }}
                          >
                            No tags added yet
                          </Typography>
                        ) : (
                          values.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              onDelete={() => {
                                const newTags = values.tags.filter(
                                  (_, i) => i !== index
                                );
                                setFieldValue("tags", newTags);
                              }}
                              sx={{
                                backgroundColor: "rgb(90, 149, 89)",
                                color: "white",
                                fontWeight: 500,
                                "& .MuiChip-deleteIcon": {
                                  color: "rgba(255, 255, 255, 0.7)",
                                  "&:hover": {
                                    color: "white",
                                  },
                                },
                              }}
                            />
                          ))
                        )}
                      </Box>
                    </Grid>

                    {/* Add New Tag */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 1.5, color: "text.secondary" }}
                      >
                        Add New Tag
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          placeholder="Enter tag name"
                          value={values.newTag}
                          onChange={(e) =>
                            setFieldValue("newTag", e.target.value)
                          }
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const trimmedTag = values.newTag.trim();
                              if (trimmedTag && !values.tags.includes(trimmedTag)) {
                                setFieldValue("tags", [...values.tags, trimmedTag]);
                                setFieldValue("newTag", "");
                              }
                            }
                          }}
                          disabled={isUpdatingTags}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: 2 },
                          }}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => {
                            const trimmedTag = values.newTag.trim();
                            if (trimmedTag && !values.tags.includes(trimmedTag)) {
                              setFieldValue("tags", [...values.tags, trimmedTag]);
                              setFieldValue("newTag", "");
                            }
                          }}
                          disabled={!values.newTag.trim() || isUpdatingTags}
                          sx={{
                            minWidth: 100,
                            borderRadius: 2,
                            borderColor: "rgb(90, 149, 89)",
                            color: "rgb(90, 149, 89)",
                            "&:hover": {
                              borderColor: "rgb(70, 120, 70)",
                              backgroundColor: "rgba(90, 149, 89, 0.04)",
                            },
                          }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Grid>

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                          pt: 2,
                          borderTop: "1px solid #e0e0e0",
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setFieldValue("tags", companyTags || []);
                            setFieldValue("newTag", "");
                          }}
                          disabled={isUpdatingTags}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            px: 3,
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={isUpdatingTags}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            px: 3,
                            backgroundColor: "rgb(90, 149, 89)",
                            "&:hover": {
                              backgroundColor: "rgba(90, 149, 89, 0.8)",
                            },
                          }}
                        >
                          {isUpdatingTags ? "Submitting..." : "Submit"}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderManageBranchTab = () => (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background:
            "linear-gradient(135deg, rgb(90, 149, 89) 0%, rgb(70, 120, 70) 100%)",
          color: "white",
        }}
      >
        <CardContent sx={{ py: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Manage Branch
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            View and manage company branches and their white label
            configurations
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 3 }}>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <DataTable
            isLoading={isManageBranchLoading}
            rows={companyBranch || []}
            rowsPerPage={rowsPerPage}
            rowsPerPageOption={rowsPerPageOption}
            total={totalCompanyManageBranch}
            page={companyManageBranchPage}
            renderHeader={() => (
              <>
                <TableCell
                  sx={{
                    fontWeight: "600",
                    color: "text.primary",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  Branch Name
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "600",
                    color: "text.primary",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  Parent Branch
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "600",
                    color: "text.primary",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  White Label
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "600",
                    color: "text.primary",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  Action
                </TableCell>
              </>
            )}
            renderRow={(row) => (
              <>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: "500" }}>
                    {row.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.parent}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                    {row.white_label || "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => onBranchWhiteLabelClick(row)}
                    sx={{
                      backgroundColor: "rgb(90, 149, 89)",
                      "&:hover": {
                        backgroundColor: "rgba(90, 149, 89, 0.8)",
                      },
                      textTransform: "none",
                      fontSize: "0.813rem",
                    }}
                  >
                    White Label
                  </Button>
                </TableCell>
              </>
            )}
            onPageClick={onCompanyManageBranchPageClick}
            handleItemPerPageChange={handleItemPerPageChange}
          />
        </Box>
      </Card>
    </Box>
  );

  const renderUsersTab = () => (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background:
            "linear-gradient(135deg, rgb(90, 149, 89) 0%, rgb(70, 120, 70) 100%)",
          color: "white",
        }}
      >
        <CardContent sx={{ py: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Users
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Search and manage company users
          </Typography>
        </CardContent>
      </Card>

      {/* Search Filter Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          backgroundColor: "white",
          border: "1px solid #e0e0e0",
        }}
      >
        <Formik
          initialValues={{
            searchColumn: query.searchColumn,
            searchValue: query.searchValue,
            status: query.status,
          }}
          enableReinitialize
          onSubmit={onSearchSubmit}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                {/* Search Column Selector */}
                <Box sx={{ minWidth: 120 }}>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                  >
                    Search By
                  </Typography>
                  <SearchableSelect
                    value={formik.values.searchColumn}
                    handleChange={(value) => {
                      formik.setFieldValue("searchColumn", value);
                    }}
                    placeholder="Column"
                    searchable={false}
                    style={{
                      height: "40px",
                      minWidth: "120px",
                      borderRadius: "8px",
                      border: "1px solid #d0d7de",
                    }}
                    panelStyle={{ minWidth: "120px" }}
                    dropdownItem={[
                      { name: "Username", id: "username" },
                      { name: "Email", id: "email" },
                      { name: "Name", id: "name" },
                    ]}
                    emptyMessage="Column Not Found"
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    isEmpty={formik.values.searchColumn === ""}
                    returnType="object"
                  />
                </Box>

                {/* Search Input */}
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                  >
                    Search Term
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Enter search term..."
                    value={formik.values.searchValue}
                    onChange={(e) =>
                      formik.setFieldValue("searchValue", e.target.value)
                    }
                    disabled={isCompanyUsersLoading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "40px",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        "&:hover fieldset": {
                          borderColor: "rgb(90, 149, 89)",
                        },
                      },
                    }}
                  />
                </Box>

                {/* Status Filter */}
                <Box sx={{ minWidth: 130 }}>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: 0.5, color: "text.secondary" }}
                  >
                    Status Filter
                  </Typography>
                  <SearchableSelect
                    value={formik.values.status}
                    handleChange={(value) => {
                      formik.setFieldValue("status", value);
                    }}
                    placeholder="All Status"
                    searchable={false}
                    style={{
                      height: "40px",
                      minWidth: "130px",
                      borderRadius: "8px",
                      border: "1px solid #d0d7de",
                    }}
                    dropdownItem={[
                      { name: "All Status", id: "" },
                      { name: "Active", id: "active" },
                      { name: "Inactive", id: "inactive" },
                      { name: "Suspended", id: "suspend" },
                      { name: "Pending", id: "pending" },
                    ]}
                    emptyMessage="Status Not Found"
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    isEmpty={formik.values.status === ""}
                    returnType="object"
                  />
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{ display: "flex", gap: 1, alignSelf: "end", pb: 0.25 }}
                >
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={onClearFilter}
                    disabled={isCompanyUsersLoading}
                    sx={{
                      height: "40px",
                      px: 3,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: "500",
                      borderColor: "#d0d7de",
                      color: "#656d76",
                      "&:hover": {
                        borderColor: "rgb(90, 149, 89)",
                        backgroundColor: "rgba(90, 149, 89, 0.04)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                      },
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="medium"
                    type="submit"
                    disabled={isCompanyUsersLoading}
                    sx={{
                      height: "40px",
                      px: 3,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: "500",
                      backgroundColor: "rgb(90, 149, 89)",
                      "&:hover": {
                        backgroundColor: "rgba(90, 149, 89, 0.8)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                      },
                    }}
                  >
                    {isCompanyUsersLoading ? "Searching..." : "Search"}
                  </Button>
                </Box>
              </Box>
            </form>
          )}
        </Formik>
      </Paper>

      {/* Users Data Table Card */}
      <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 3 }}>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {!isCompanyUsersLoading &&
          (!companyUsers || companyUsers.length === 0) ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
                No Users Found
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", maxWidth: 300 }}
              >
                {query.searchValue || query.status.id
                  ? "No users match your current search criteria. Try adjusting your filters."
                  : "This company doesn't have any users yet."}
              </Typography>
            </Box>
          ) : (
            <DataTable
              isLoading={isCompanyUsersLoading}
              rows={companyUsers || []}
              rowsPerPage={usersRowsPerPage}
              rowsPerPageOption={rowsPerPageOption}
              total={totalUsers}
              page={currentUsersPage}
              renderHeader={() => (
                <>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      color: "text.primary",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      color: "text.primary",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    User Details
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      color: "text.primary",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    User Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      color: "text.primary",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    Role
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      color: "text.primary",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "600",
                      color: "text.primary",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    QR Credit Balance
                  </TableCell>
                  {(canTransferCredit || canImpersonate) && (
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        color: "text.primary",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </>
              )}
              renderRow={(row, index) => (
                <>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ py: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "600",
                          fontSize: "0.875rem",
                          color: "rgba(0, 0, 0, 1)",
                          mb: 0.25,
                        }}
                      >
                        {row.username}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.75rem",
                          mb: row.name ? 0.25 : 0,
                        }}
                      >
                        {row.email}
                      </Typography>
                      {row.name && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            color: "text.primary",
                            fontStyle: "italic",
                          }}
                        >
                          {row.name}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.userType}</TableCell>
                  <TableCell>{row.role ? row.role : "-"}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        fontWeight: "500",
                        textTransform: "capitalize",
                        backgroundColor:
                          row.status === "active"
                            ? "#e8f5e8"
                            : row.status === "suspend"
                            ? "#ffebee"
                            : row.status === "pending"
                            ? "#fff3e0"
                            : "#f5f5f5",
                        color:
                          row.status === "active"
                            ? "#2e7d32"
                            : row.status === "suspend"
                            ? "#c62828"
                            : row.status === "pending"
                            ? "#ef6c00"
                            : "#757575",
                      }}
                    >
                      {row.status}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: "500" }}>
                      {getCreditAmount(row)}
                    </Typography>
                  </TableCell>
                  {(canTransferCredit || canImpersonate) && (
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          handleActionMenuOpen(event, index);
                        }}
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            backgroundColor: "rgba(90, 149, 89, 0.04)",
                            color: "rgb(90, 149, 89)",
                          },
                        }}
                      >
                        <MoreHorizIcon />
                      </IconButton>
                      <Popper
                        open={
                          Boolean(actionMenuAnchor) &&
                          index === selectedUserIndex
                        }
                        anchorEl={actionMenuAnchor}
                        onClose={handleActionMenuClose}
                        placement="left-start"
                        style={{ zIndex: 99999999 }}
                      >
                        <Box className={classes.boxSubmenu}>
                          <Paper className={classes.paper}>
                            <ClickAwayListener
                              onClickAway={handleActionMenuClose}
                            >
                              <MenuList>
                                {[
                                  {
                                    label: "Credit Transfer",
                                    condition:
                                      (!selectedCompanyForUsers
                                        || selectedCompanyForUsers.companyPlan
                                          !== "Free Saas") && canTransferCredit,
                                    onClick: () => onCreditTransferClick(row),
                                  },
                                  {
                                    label: "Impersonate User",
                                    condition: canImpersonate,
                                    onClick: () => handleImpersonate(row.sub),
                                  },
                                ].map((item, itemIndex) =>
                                  item.condition !== undefined ? (
                                    item.condition && (
                                      <MenuItem
                                        className={classes.menuItem}
                                        key={itemIndex}
                                        onClick={(e) => {
                                          item.onClick();
                                          handleActionMenuClose();
                                        }}
                                      >
                                        {item.label}
                                      </MenuItem>
                                    )
                                  ) : (
                                    <MenuItem
                                      className={classes.menuItem}
                                      key={itemIndex}
                                      onClick={(e) => {
                                        item.onClick();
                                        handleActionMenuClose();
                                      }}
                                    >
                                      {item.label}
                                    </MenuItem>
                                  )
                                )}
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Box>
                      </Popper>
                    </TableCell>
                  )}
                </>
              )}
              onPageClick={onCompanyUsersPageClick}
              handleItemPerPageChange={handleUsersItemPerPageChange}
            />
          )}
        </Box>
      </Card>
    </Box>
  );

  const renderExpiredManagementTab = () => {
    const originalExpiredAt = (companyData && companyData.company && companyData.company.expired_at) || "";

    const parseDate = (str) => {
      if (!str || !/^\d{4}-\d{2}-\d{2}$/.test(str)) return null;
      const [y, m, d] = str.split("-").map(Number);
      return new Date(y, m - 1, d);
    };

    const getPreviewBusinessLogic = (months) => {
      if (!months || Number.isNaN(months) || months <= 0) return "-";
      const now = new Date();
      const expiredDate = parseDate(originalExpiredAt);
      let baseDate;

      if (expiredDate && expiredDate > now) {
        baseDate = new Date(expiredDate);
      } else {
        baseDate = new Date(now);
      }

      baseDate.setMonth(baseDate.getMonth() + Number(months));
      const year = baseDate.getFullYear();
      const month = String(baseDate.getMonth() + 1).padStart(2, "0");
      const day = String(baseDate.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const handleSave = () => {
      const num = Number(monthsInput);
      if (!num || num <= 0) {
        return;
      }
      const newDate = getPreviewBusinessLogic(num);
      if (newDate === "-") return;

      handleSaveExpiredDate(newDate);
    };

    const previewDate = getPreviewBusinessLogic(Number(monthsInput));

    return (
      <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
        <Card
          sx={{
            mb: 3,
            background:
              "linear-gradient(135deg, rgb(90, 149, 89) 0%, rgb(70, 120, 70) 100%)",
            color: "white",
          }}
        >
          <CardContent sx={{ py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Expired Management
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Manage company expiration date and validity period
            </Typography>
          </CardContent>
        </Card>

        {loadingExpired ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Expired Date"
                    value={originalExpiredAt || ""}
                    variant="outlined"
                    disabled
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>

                {!editMode ? (
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        variant="contained"
                        onClick={() => setEditMode(true)}
                        sx={{
                          backgroundColor: "rgb(90, 149, 89)",
                          "&:hover": {
                            backgroundColor: "rgba(90, 149, 89, 0.8)",
                          },
                        }}
                      >
                        Edit Expired Date
                      </Button>
                    </Box>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#e3f2fd",
                          borderRadius: 2,
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, color: "#1976d2", mb: 1 }}
                        >
                          Preview - {previewDate !== "-" ? previewDate : ""}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#424242" }}>
                          {previewDate !== "-"
                            ? "Extending from current expired date."
                            : "Enter valid number of months to see preview"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Change Expired Validity (Months)"
                        placeholder="Enter number of months, e.g. 12, 24, 36 (from current expired date)"
                        variant="outlined"
                        value={monthsInput}
                        onChange={(e) => setMonthsInput(e.target.value)}
                        type="number"
                        disabled={updatingExpired}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setEditMode(false);
                            setMonthsInput("");
                          }}
                          disabled={updatingExpired}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSave}
                          disabled={
                            updatingExpired ||
                            !monthsInput ||
                            Number(monthsInput) <= 0 ||
                            previewDate === "-"
                          }
                          sx={{
                            backgroundColor: "rgb(90, 149, 89)",
                            "&:hover": {
                              backgroundColor: "rgba(90, 149, 89, 0.8)",
                            },
                          }}
                        >
                          {updatingExpired ? "Saving..." : "EDIT"}
                        </Button>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  const renderSalesEmailTab = () => {
    return (
      <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
        <Card
          sx={{
            mb: 3,
            background:
              "linear-gradient(135deg, rgb(90, 149, 89) 0%, rgb(70, 120, 70) 100%)",
            color: "white",
          }}
        >
          <CardContent sx={{ py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Sales Email
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Update the sales email for this company
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Formik
              initialValues={{
                salesEmail:
                  (selectedCompany.company && selectedCompany.company.sales_email) ||
                  selectedCompany.sales_email ||
                  "",
              }}
              validationSchema={Yup.object().shape({
                salesEmail: Yup.string()
                  .email("Invalid sales email")
                  .required("Sales Email is required"),
              })}
              onSubmit={handleSalesEmailSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit: formikSubmit,
              }) => (
                <form onSubmit={formikSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        variant="outlined"
                        label="Sales Email"
                        name="salesEmail"
                        fullWidth
                        value={values.salesEmail}
                        required
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isSubmittingSalesEmail}
                        error={touched.salesEmail && Boolean(errors.salesEmail)}
                        helperText={touched.salesEmail && errors.salesEmail}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                        }}
                      >
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={isSubmittingSalesEmail}
                          sx={{
                            backgroundColor: "rgb(90, 149, 89)",
                            "&:hover": {
                              backgroundColor: "rgba(90, 149, 89, 0.8)",
                            },
                          }}
                        >
                          {isSubmittingSalesEmail ? "Saving..." : "Save"}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderCompanyFeaturesTab = () => {
    return (
      <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
        <Card
          sx={{
            mb: 3,
            background:
              "linear-gradient(135deg, rgb(90, 149, 89) 0%, rgb(70, 120, 70) 100%)",
            color: "white",
          }}
        >
          <CardContent sx={{ py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Company Features
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Choose the bundle for this company, or switch modules on and off
              to save a new one
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 3, mb: 1 }}>
          <CardContent sx={{ p: 3 }}>
            {companyFeature ? (
              <Formik
                initialValues={bundleSelectionInitialValues(
                  bundleOptions,
                  companyFeature
                )}
                validate={validateBundleSelection(bundleOptions)}
                onSubmit={handleCompanyFeatureSubmit}
                enableReinitialize
              >
                {(formik) => (
                  <form onSubmit={formik.handleSubmit}>
                    {/* The same editor the standalone Company Bundle dialog
                        renders — one implementation, so this panel cannot go
                        read-only again while the other stays editable. The
                        Properties tab that used to sit here was V2 leftover:
                        the Rev 4 read endpoint returns no `properties` and the
                        write endpoint reads none, so it always rendered "No
                        properties available" and saved nothing. */}
                    <Box
                      sx={{
                        backgroundColor: "#f3f3f3",
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <BundleSelectionEditor
                        formik={formik}
                        bundleOptions={bundleOptions}
                        moduleListing={moduleListing}
                        isLoaded={isCompanyFeatureLoaded}
                        isLoading={isCompanyFeatureLoading}
                        hasFailed={hasCompanyFeatureLoadFailed}
                        onRetry={handleCompanyFeatureRetry}
                        disabled={isUpdatingCompanyFeature}
                      />
                    </Box>

                    {/* Decision #19: a company on no bundle keeps whatever grants it
                        already holds. The bundle Select above stays the only live
                        control (assigning a bundle is still allowed and applies
                        replace-set semantics), but there is no module set to draw, so
                        show the company's CURRENT effective access as a read-only tree.
                        `disabled` forwards to every Checkbox/Radio in the panel, and no
                        part of this feeds the save payload — `setCompanyBundle` reads
                        only the bundle id. Rendered as a sibling here rather than inside
                        BundleSelectionEditor, so the editor is untouched for the assigned
                        case. */}
                    {formik.values.bundle === NO_BUNDLE && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="textSecondary">
                          No bundle assigned. Showing this company's current
                          effective access (read-only).
                        </Typography>
                        <Box
                          sx={{
                            backgroundColor: "#f3f3f3",
                            borderRadius: 2,
                            p: 2,
                            mt: 1,
                          }}
                        >
                          <CompanyFeaturePanel
                            features={featureStructure || []}
                            enabledList={companyFeature.enabledAccessRight}
                            disabled
                            layer={5}
                          />
                        </Box>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 3,
                      }}
                    >
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={isUpdatingCompanyFeature}
                        sx={{
                          backgroundColor: "rgb(90, 149, 89)",
                          "&:hover": {
                            backgroundColor: "rgba(90, 149, 89, 0.8)",
                          },
                        }}
                      >
                        {isUpdatingCompanyFeature ? "Saving..." : "Save"}
                      </Button>
                    </Box>
                  </form>
                )}
              </Formik>
            ) : companyFeatureError ? (
              /* The read failed and there is nothing to seed the panel from. A spinner here
                 would never resolve, so say what happened and offer the way out. This is
                 only ever the FIRST read of a company: a re-read keeps the answer already on
                 screen, so a save that worked cannot land the admin here. */
              <Box my={2}>
                <Alert
                  severity="error"
                  action={(
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleCompanyFeatureRetry}
                    >
                      Retry
                    </Button>
                  )}
                >
                  {companyFeatureError}
                </Alert>
              </Box>
            ) : (
              <Box my={4}>
                <Loading />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderSwapPlanTab = () => {
    return (
      <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
        <Card
          sx={{
            mb: 3,
            background:
              "linear-gradient(135deg, rgb(90, 149, 89) 0%, rgb(70, 120, 70) 100%)",
            color: "white",
          }}
        >
          <CardContent sx={{ py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Swap Plan
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Change the subscription plan for this company
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            {isLoadingSwapPlan ? (
              <>
                <Skeleton variant="text" animation="wave" height={25} />
                <Skeleton variant="text" animation="wave" height={25} />
                <Skeleton variant="text" animation="wave" height={25} />
                <Skeleton variant="text" animation="wave" height={25} />
              </>
            ) : !plans || plans.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="textSecondary">
                  Unable to load available plans. You may not have permission to
                  access this feature.
                </Typography>
              </Box>
            ) : (
              <Formik
                initialValues={{
                  plan: plans && plans.length ? plans[0].uuid : "",
                  need_access_right_update: false,
                }}
                enableReinitialize
                onSubmit={handleSwapPlanSubmit}
              >
                {(formik) => (
                  <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          <strong>Current Plan:</strong>{" "}
                          {planInfo ? planInfo.plan_name : "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="New Plan"
                          name="plan"
                          onChange={(event) => {
                            formik.setFieldValue("plan", event.target.value);
                          }}
                          required
                          select
                          SelectProps={{ native: true }}
                          value={formik.values.plan}
                          variant="outlined"
                          disabled={isUpdatingSwapPlan}
                          sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: 2 },
                          }}
                          inputProps={{
                            style: { textTransform: "capitalize" },
                          }}
                        >
                          {(plans || []).map((plan, i) => (
                            <option
                              value={plan.uuid}
                              key={i}
                              style={{ textTransform: "capitalize" }}
                            >
                              {plan.name}
                            </option>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                          <Checkbox
                            checked={!formik.values.need_access_right_update}
                            onChange={(event, checked) =>
                              formik.setFieldValue(
                                "need_access_right_update",
                                !checked
                              )
                            }
                          />
                          <Typography variant="body2">
                            Maintain current access right when switching plan
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                          }}
                        >
                          <Button
                            variant="contained"
                            type="submit"
                            disabled={isUpdatingSwapPlan}
                            sx={{
                              backgroundColor: "rgb(90, 149, 89)",
                              "&:hover": {
                                backgroundColor: "rgba(90, 149, 89, 0.8)",
                              },
                            }}
                          >
                            {isUpdatingSwapPlan ? "Updating..." : "Update Plan"}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Formik>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={!!selectedCompany}
        onClose={onCompanyDetailsDialogCloseClick}
        scroll="paper"
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        fullWidth
        maxWidth="xl"
      >
        <Box
          sx={{
            p: 2,
            backgroundColor: "rgb(90, 149, 89)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, flex: 1, textAlign: "center" }}
          >
            View/Edit Companies Details
          </Typography>
          <IconButton
            size="small"
            sx={{ color: "white" }}
            onClick={onCompanyDetailsDialogCloseClick}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          dividers
          sx={{ p: 0, height: "80vh", overflow: "hidden" }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* Main Content Area */}
            <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
              {/* Tab Configuration */}
              {(() => {
                const tabConfig = [
                  { label: "Company Information", index: 0, render: renderCompanyDetailsPanel },
                  { label: "Company Configuration", index: 1, render: renderConfigurationTab },
                  { label: "Company Features", index: 2, render: renderCompanyFeaturesTab },
                  { label: "Manage Branch", index: 3, render: renderManageBranchTab },
                  { label: "Users", index: 4, render: renderUsersTab },
                  { label: "Expired Management", index: 5, render: renderExpiredManagementTab },
                  { label: "Sales Email", index: 6, render: renderSalesEmailTab },
                  { label: "Swap Plan", index: 7, render: renderSwapPlanTab },
                ];

                return (
                  <>
                    {/* Left Sidebar Menu */}
                    <Box
                      sx={{
                        width: "300px",
                        minWidth: "300px",
                        borderRight: "1px solid #e0e0e0",
                        backgroundColor: "#f8f9fa",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* Sidebar Menu Items */}
                      <Box sx={{ paddingTop: 2 }}>
                        {tabConfig.map((item) => (
                          <Box
                            key={item.index}
                            onClick={() => onTabChange(null, item.index)}
                            sx={{
                              p: 2,
                              borderBottom: "1px solid #e0e0e0",
                              cursor: "pointer",
                              backgroundColor:
                                currentTab === item.index
                                  ? "rgba(90, 149, 89, 0.1)"
                                  : "transparent",
                              "&:hover": {
                                backgroundColor: "rgba(90, 149, 89, 0.05)",
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: currentTab === item.index ? "600" : "400",
                                color:
                                  currentTab === item.index
                                    ? "rgb(90, 149, 89)"
                                    : "text.primary",
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Empty space at bottom */}
                      <Box sx={{ flex: 1 }}></Box>
                    </Box>

                    {/* Right Main Display Area */}
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "white",
                        overflow: "hidden",
                      }}
                    >
                      <Box sx={{ flex: 1, overflow: "hidden" }}>
                        {tabConfig.map((item) => (
                          <TabPanel key={item.index} value={currentTab} index={item.index}>
                            {item.render()}
                          </TabPanel>
                        ))}
                      </Box>
                    </Box>
                  </>
                );
              })()}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <CompanyWhiteLabelDialog
        selectedCompany={selectedBranchWhiteLabel}
        onCompanyWhiteLabelDialogSubmitClick={
          onBranchWhiteLabelDialogSubmitClick
        }
        onCompanyWhiteLabelDialogCloseClick={onBranchWhiteLabelDialogCloseClick}
        onRemoveBranchWhiteLabelClick={setConfirmationDialog}
        isRemoving={isRemovingBranch}
      />

      <ConfirmationDialogComponent
        isOpen={confirmationDialogModal}
        handleClose={closeConfirmationDialog}
        handleProceed={onRemoveBranchWhiteLabelClick}
        title="Are you sure to remove the white label?"
      />

      <UserCreditTransferContainer
        selectedCompanyUser={selectedCompanyUser}
        onCreditTransferCloseClick={onCreditTransferCloseClick}
        openCreditTransferConfirmation={openCreditTransferConfirmation}
        onCreditTransferConfirmationClick={onCreditTransferConfirmationClick}
        onCreditTransferConfirmSubmitClick={onCreditTransferConfirmSubmitClick}
        isSubmitting={isSubmitting}
        onCreditTransferConfirmationCloseClick={
          onCreditTransferConfirmationCloseClick
        }
        completeDialogErrors={completeDialogErrors}
        openCompleteDialog={openCompleteDialog}
        onCompleteDialogClose={onCompleteDialogClose}
      />
    </>
  );
};

CompanyDetailsDialogComponent.propTypes = {
  industries: PropTypes.array.isRequired,
  selectedCompany: PropTypes.object,
  onCompanyDetailsDialogCloseClick: PropTypes.func.isRequired,
  currentTab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onCompanyWhiteLabelDialogSubmitClick: PropTypes.func.isRequired,
  onRemoveCompanyWhiteLabelClick: PropTypes.func.isRequired,
  onCompanyUsersViewClick: PropTypes.func.isRequired,
  selectedCompanyForUsers: PropTypes.object,
  handleImpersonate: PropTypes.func,
  canImpersonate: PropTypes.bool,
  canTransferCredit: PropTypes.bool,
  // Expired Management props
  editMode: PropTypes.bool,
  setEditMode: PropTypes.func,
  monthsInput: PropTypes.string,
  setMonthsInput: PropTypes.func,
  companyData: PropTypes.object,
  loadingExpired: PropTypes.bool,
  updatingExpired: PropTypes.bool,
  handleSaveExpiredDate: PropTypes.func,
  // Sales Email props
  isSubmittingSalesEmail: PropTypes.bool,
  handleSalesEmailSubmit: PropTypes.func,
  // Company Features props
  bundleOptions: PropTypes.array,
  moduleListing: PropTypes.array,
  featureStructure: PropTypes.array,
  companyFeature: PropTypes.object,
  companyFeatureError: PropTypes.string,
  isCompanyFeatureLoaded: PropTypes.bool,
  hasCompanyFeatureLoadFailed: PropTypes.bool,
  isCompanyFeatureLoading: PropTypes.bool,
  isUpdatingCompanyFeature: PropTypes.bool,
  handleCompanyFeatureSubmit: PropTypes.func,
  handleCompanyFeatureRetry: PropTypes.func,
  // Swap Plan props
  planInfo: PropTypes.object,
  plans: PropTypes.array,
  isLoadingSwapPlan: PropTypes.bool,
  isUpdatingSwapPlan: PropTypes.bool,
  handleSwapPlanSubmit: PropTypes.func,
};

export default CompanyDetailsDialogComponent;
